import { useDispatch } from "react-redux";
import { GET_FAQ_DEFAULT_PAGE_SIZE } from "../../../utils/constants";
import { getFaqs } from "../../../core/actionCreators/faqActionCreator";
import { useEffect, useState, useRef } from "react";
import { useSelector } from "react-redux";
import queryString from 'query-string';
import CommonPagination from "../CommonPagination/CommonPagination";
import Loader from "../Loader/Loader";
import FaqListItem from "./faqListItem";
import { removeEmptyStringKeys } from "../../../utils/helper";

const EMPTY_FILTERS = {
    categoryCode: "",
    categoryName: "",
}

const FILTER_DEBOUNCE_MS = 600;

const INPUT_CLASS = "border border-gray-300 h-8 rounded-sm focus:outline-none focus:border-blue-400 px-1.5 text-xs w-full";

const FaqSchemaList = () => {
    const dispatch = useDispatch()
    const { faqs, isLoading } = useSelector((s: any) => s.faqStore)

    const [filters, setFilters] = useState({ ...EMPTY_FILTERS } as any)
    const [pageNumber, setPageNumber] = useState(0)

    // Typing should settle before firing a request; paging should not wait.
    const isFilterChangeRef = useRef(false);

    useEffect(() => {
        const delay = isFilterChangeRef.current ? FILTER_DEBOUNCE_MS : 0;
        const timeout = setTimeout(() => {
            isFilterChangeRef.current = false;
            dispatch(getFaqs(
                queryString.stringify(removeEmptyStringKeys({
                    ...filters,
                    pageNumber,
                    pageSize: GET_FAQ_DEFAULT_PAGE_SIZE,
                })),
                null
            ));
        }, delay);
        return () => clearTimeout(timeout);
    }, [filters, pageNumber, dispatch]);

    // A changed filter re-runs the search from the first page — keeping the old
    // page number would land the user on an empty tail of a shorter result set.
    const onFilterChange = (key: string, value: string) => {
        isFilterChangeRef.current = true;
        setFilters({ ...filters, [key]: value });
        setPageNumber(0);
    }

    const hasActiveFilters = Object.keys(EMPTY_FILTERS).some((key) => !!filters[key]);

    const clearFilters = () => {
        isFilterChangeRef.current = false;
        setFilters({ ...EMPTY_FILTERS });
        setPageNumber(0);
    }

    // CommonPagination counts pages from 1, the API counts them from 0.
    const onPageChange = (page: any) => {
        setPageNumber(Math.max(0, Number(page) - 1));
    }

    return (
        <>
            <div className="mt-2.5 bg-white px-4 py-3 flex flex-wrap items-end gap-x-4 gap-y-3">
                <div className="w-[220px]">
                    <label className="block text-[11px] text-gray-600 mb-1">Category Code</label>
                    <input
                        type="text"
                        placeholder="Search category code"
                        value={filters.categoryCode}
                        onChange={(e) => onFilterChange("categoryCode", e.target.value)}
                        className={INPUT_CLASS}
                    />
                </div>
                <div className="w-[220px]">
                    <label className="block text-[11px] text-gray-600 mb-1">Category Name</label>
                    <input
                        type="text"
                        placeholder="Search category name"
                        value={filters.categoryName}
                        onChange={(e) => onFilterChange("categoryName", e.target.value)}
                        className={INPUT_CLASS}
                    />
                </div>
                <button
                    onClick={clearFilters}
                    disabled={!hasActiveFilters}
                    className="h-8 px-4 rounded text-xs font-medium bg-gray-200 text-gray-700 hover:bg-gray-300 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                    Clear Filters
                </button>
            </div>
            <div className="mt-2.5 campaign-list bg-white mb-[54px]">
                <table className="w-full">
                    <thead className="bg-[#343434]">
                        <tr>
                            <th className="px-4 py-3 text-left text-xs font-semibold text-white w-[40%]">Category Code</th>
                            <th className="px-4 py-3 text-left text-xs font-semibold text-white w-[40%]">Category Name</th>
                            <th className="px-4 py-3 text-left text-xs font-semibold text-white w-[10%]">Count</th>
                            <th className="px-4 py-3 text-left text-xs font-semibold text-white w-[10%]">Action</th>
                        </tr>
                    </thead>
                    <tbody>
                        {isLoading ? (
                            <tr>
                                <td colSpan={4} className="py-12">
                                    <div className="flex flex-col items-center justify-center space-y-2">
                                        <Loader />
                                        <span className="text-xs text-gray-500">Loading FAQ schemas...</span>
                                    </div>
                                </td>
                            </tr>
                        ) : (
                            <FaqListItem data={faqs?.data || []} />
                        )}
                    </tbody>
                </table>
            </div>
            {faqs.data && <CommonPagination data={faqs} PAGE_SIZE={GET_FAQ_DEFAULT_PAGE_SIZE} onPageChange={onPageChange} />}
        </>
    )
}
export default FaqSchemaList;
